import { useCallback, useState } from 'react';

export type McpConsentStep = 'select' | 'review';

export type ConsentStepState = {
  /**
   * Where the user is in the two-step consent that replaces the single screen
   * when it is enabled: `select` shows the selectors while permissions load in
   * the background, `review` shows only the permissions. Always `select`
   * outside that flow.
   */
  consentStep: McpConsentStep;
  /** On the selectors step of the two-step consent. False in single-step flows. */
  isSelectStep: boolean;
  /** On the permissions step of the two-step consent. False in single-step flows. */
  isReviewStep: boolean;
  goToReview: () => void;
  goToSelect: () => void;
};

/**
 * Step of the two-step MCP consent. Outside the two-step flow the step is
 * always `select`, whatever `goToReview` was asked for, so a single-step flow
 * can't land on a review screen it has no selectors for.
 */
export function useConsentStep(
  isTwoStepConsentEnabled: boolean,
): ConsentStepState {
  const [requestedStep, setRequestedStep] = useState<McpConsentStep>('select');
  const goToReview = useCallback(() => setRequestedStep('review'), []);
  const goToSelect = useCallback(() => setRequestedStep('select'), []);

  const consentStep = isTwoStepConsentEnabled ? requestedStep : 'select';
  return {
    consentStep,
    isSelectStep: isTwoStepConsentEnabled && consentStep === 'select',
    isReviewStep: consentStep === 'review',
    goToReview,
    goToSelect,
  };
}
